import React from 'react';
import { Link } from 'react-router-dom';
import { Monitor, CheckCircle2, Users, Lock } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import ProgressBar from './ProgressBar';
import { getImageUrl } from '../config';

const STATUS_LABELS = {
  active: 'En cours',
  threshold_reached: 'Seuil atteint',
  validated: 'Validé',
  delivered: 'Livré',
};

export default function DealCard({ deal, comingSoon = false }) {
  const isActive = deal.status === 'active';
  const isCompleted = ['validated', 'delivered'].includes(deal.status);
  const product = deal.product;
  const mainImage = product?.images?.[0] ? getImageUrl(product.images[0]) : null;
  const saving = deal.base_price - (deal.current_price || deal.base_price);

  const cardClasses = `group relative bg-white rounded-sm border overflow-hidden transition-all duration-300 block ${
    comingSoon
      ? 'border-slate-200 opacity-90 cursor-not-allowed select-none'
      : isActive
        ? 'border-secondary/30 hover:border-secondary/50 hover:shadow-secondary/10 hover:shadow-2xl hover:-translate-y-1'
        : 'border-slate-200 hover:border-slate-300 opacity-80 hover:shadow-2xl hover:-translate-y-1'
  }`;

  const inner = (
    <>
      {/* Image */}
      <div className="aspect-[16/9] relative overflow-hidden bg-slate-100">
        {mainImage ? (
          <img
            src={mainImage}
            alt={deal.title}
            className={`w-full h-full object-cover transition-transform duration-500 ${
              comingSoon ? '' : 'group-hover:scale-105'
            }`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Monitor className="w-16 h-16 text-slate-300" />
          </div>
        )}

        {/* Status badge */}
        <div className="absolute top-3 left-3">
          {isActive ? (
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-secondary/15 text-secondary border border-secondary/40 backdrop-blur-sm">
              {STATUS_LABELS[deal.status]}
            </span>
          ) : isCompleted ? (
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 backdrop-blur-sm flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {STATUS_LABELS[deal.status]}
            </span>
          ) : null}
        </div>

        {/* Saving badge */}
        {saving > 0 && (
          <div className="absolute top-3 right-3">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white shadow-lg">
              -{saving.toFixed(0)}€/unité
            </span>
          </div>
        )}

        {/* Completed overlay */}
        {isCompleted && !comingSoon && (
          <div className="absolute inset-0 bg-black/30" />
        )}

        {/* Coming soon overlay + ribbon */}
        {comingSoon && (
          <>
            <div className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" />
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-3">
              <div className="flex items-center justify-center gap-2 bg-secondary text-white font-bold uppercase tracking-wider text-[11px] md:text-xs py-2 rounded-md shadow-lg">
                <Lock className="w-3.5 h-3.5" />
                <span>Bientôt disponible</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className={`font-heading font-semibold uppercase text-primary text-base leading-snug line-clamp-2 transition-colors ${
          comingSoon ? '' : 'group-hover:text-secondary'
        }`}>
          {deal.title}
        </h3>

        {/* Pricing */}
        <div className="flex items-baseline gap-2">
          <span className="font-heading font-bold text-xl text-secondary">
            {(deal.current_price || deal.base_price).toFixed(2)} €
          </span>
          {saving > 0 && (
            <span className="text-sm text-slate-500 line-through">{deal.base_price.toFixed(2)} €</span>
          )}
        </div>

        {/* Progress */}
        {isActive && deal.min_threshold && (
          <ProgressBar
            current={deal.confirmed_quantity || 0}
            target={deal.min_threshold}
          />
        )}

        {/* Completed stats */}
        {isCompleted && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users className="w-4 h-4" />
            <span>{deal.current_participants || deal.confirmed_quantity || 0} participants</span>
          </div>
        )}

        {/* Countdown */}
        {isActive && deal.expires_at && !comingSoon && (
          <CountdownTimer expiresAt={deal.expires_at} />
        )}

        {/* Teaser footer */}
        {comingSoon && (
          <p className="text-xs text-slate-500 italic">Illustration — fonctionnalité en préparation</p>
        )}
      </div>
    </>
  );

  if (comingSoon) {
    return (
      <div className={cardClasses} aria-disabled="true" data-testid="deal-card-coming-soon">
        {inner}
      </div>
    );
  }

  return (
    <Link to={`/deals/${deal.deal_id}`} className={cardClasses}>
      {inner}
    </Link>
  );
}
